import { convertLetters } from "@/utils/letterConversion";
import { gql, useQuery } from "@apollo/client";
import {
    ArrowLeft,
    Calendar,
    Clock,
    ExternalLink,
    Share2,
    User,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";

const STREAM_TEAM_EVENT_QUERY = gql`
    query StreamTeamEvent($eventId: UUID!) {
        streamTeamEvent(eventId: $eventId) {
            id
            createdBy {
                id
                player {
                    id
                    name
                    avatarUrl
                }
                twitchUsername
            }
            title
            description
            summary
            eventType
            status
            locationType
            locationDetails
            startTime
            endTime
            joinUrl
            joinInstructions
            isPast
            isUpcoming
            isLiveNow
            createdAt
        }
    }
`;

interface StreamTeamEvent {
    id: string;
    createdBy: {
        id: string;
        player: {
            id: string;
            name: string;
            avatarUrl: string | null;
        };
        twitchUsername: string;
    };
    title: string;
    description: string;
    summary: string;
    eventType: string;
    status: string;
    locationType: string;
    locationDetails: string;
    startTime: string;
    endTime: string | null;
    joinUrl: string;
    joinInstructions: string;
    isPast: boolean;
    isUpcoming: boolean;
    isLiveNow: boolean;
    createdAt: string;
}

const EVENT_TYPE_LABELS: Record<string, string> = {
    subathon: "Subathon",
    charity: "Charity Event",
    competition: "Competition",
    community: "Community Event",
    tournament: "Tournament",
    collaboration: "Collaboration",
    other: "Other",
};

const STATUS_LABELS: Record<string, string> = {
    draft: "Draft",
    scheduled: "Scheduled",
    live: "Live",
    completed: "Completed",
    cancelled: "Cancelled",
};

const LOCATION_TYPE_LABELS: Record<string, string> = {
    online: "Online",
    in_person: "In Person",
    hybrid: "Hybrid",
};

function formatEventDate(startTime: string, endTime: string | null): string {
    const start = new Date(startTime);
    const startFormatted = start.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
    });
    const startTimeFormatted = start.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
    });

    if (endTime) {
        const end = new Date(endTime);
        const isSameDay = start.toDateString() === end.toDateString();
        if (isSameDay) {
            const endTimeFormatted = end.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
            });
            return `${startFormatted} • ${startTimeFormatted} - ${endTimeFormatted}`;
        } else {
            const endFormatted = end.toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
            });
            const endTimeFormatted = end.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
            });
            return `Starts: ${startFormatted} at ${startTimeFormatted}\nEnds: ${endFormatted} at ${endTimeFormatted}`;
        }
    }
    return `${startFormatted} at ${startTimeFormatted}`;
}

function getTimeUntilOrSince(
    startTime: string,
    endTime: string | null,
    isPast: boolean,
): string {
    const now = new Date();
    const start = new Date(startTime);

    if (isPast) {
        const end = endTime ? new Date(endTime) : start;
        const diffMs = now.getTime() - end.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffWeeks = Math.floor(diffDays / 7);
        if (diffWeeks > 0) {
            return `Ended ${diffWeeks} week${diffWeeks > 1 ? "s" : ""} ago`;
        }
        if (diffDays > 0) {
            return `Ended ${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
        }
        return "Ended today";
    }

    const diffMs = start.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) {
        return `Starts in ${diffDays} day${diffDays > 1 ? "s" : ""}`;
    }
    if (diffHours > 0) {
        return `Starts in ${diffHours} hour${diffHours > 1 ? "s" : ""}`;
    }
    if (diffMinutes > 0) {
        return `Starts in ${diffMinutes} minute${diffMinutes > 1 ? "s" : ""}`;
    }
    return "Starting now";
}

export default function StreamTeamEventDetailPage() {
    const { eventId } = useParams<{ eventId: string }>();

    const { data, loading, error } = useQuery<{
        streamTeamEvent: StreamTeamEvent;
    }>(STREAM_TEAM_EVENT_QUERY, {
        variables: { eventId },
        skip: !eventId,
    });

    if (loading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <div className="text-center">
                    <div className="border-brand inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]" />
                    <p className="text-stats font-garamond mt-4 text-lg">
                        Loading event...
                    </p>
                </div>
            </div>
        );
    }

    if (error || !data?.streamTeamEvent) {
        return (
            <div className="mx-auto w-full px-4 py-8 sm:px-6 sm:py-12 lg:w-3/4">
                <Link
                    to="/events"
                    className="text-brand hover:text-brand/80 mb-8 inline-flex items-center gap-2 font-medium transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Events
                </Link>
                <div className="border-brand_stream_team rounded-xl border-2 bg-zinc-900 p-12 text-center">
                    <h1 className="font-prviking text-brand mb-4 text-3xl">
                        {convertLetters("Event Not Found")}
                    </h1>
                    <p className="font-garamond text-zinc-400">
                        The event you're looking for doesn't exist or has been
                        removed.
                    </p>
                </div>
            </div>
        );
    }

    const event = data.streamTeamEvent;

    return (
        <div className="mx-auto w-full px-4 py-8 sm:px-6 sm:py-12 lg:w-3/4">
            {/* Back link */}
            <Link
                to="/events"
                className="text-brand hover:text-brand/80 mb-8 inline-flex items-center gap-2 font-medium transition-colors"
            >
                <ArrowLeft className="h-4 w-4" />
                Back to Events
            </Link>

            {/* Header */}
            <div className="mb-8 text-center">
                <h1 className="font-prviking text-brand mb-4 text-3xl sm:text-5xl lg:text-6xl">
                    {convertLetters(event.title)}
                </h1>
                {event.summary && (
                    <p className="font-garamond mx-auto max-w-3xl text-lg text-zinc-300 italic">
                        "{event.summary}"
                    </p>
                )}

                {/* Status badge */}
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                    {event.isLiveNow ? (
                        <span className="flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-bold text-white">
                            <span className="h-2 w-2 animate-pulse rounded-full bg-white"></span>
                            HAPPENING NOW
                        </span>
                    ) : event.isPast ? (
                        <span className="flex items-center gap-2 rounded-full border border-zinc-600 bg-zinc-700 px-4 py-2 text-sm font-medium text-zinc-300">
                            <Clock className="h-4 w-4" />
                            {getTimeUntilOrSince(
                                event.startTime,
                                event.endTime,
                                event.isPast,
                            )}
                        </span>
                    ) : (
                        <span className="flex items-center gap-2 rounded-full border border-green-600/50 bg-green-900/30 px-4 py-2 text-sm font-medium text-green-400">
                            <Clock className="h-4 w-4" />
                            {getTimeUntilOrSince(
                                event.startTime,
                                event.endTime,
                                event.isPast,
                            )}
                        </span>
                    )}
                </div>
            </div>

            {/* Main content grid */}
            <div className="grid gap-6 lg:grid-cols-3">
                {/* Date & Time */}
                <div className="border-brand_stream_team rounded-xl border-2 bg-zinc-900 p-6">
                    <h2 className="text-brand mb-4 flex items-center gap-2 text-lg font-bold">
                        <Calendar className="h-5 w-5" />
                        Date & Time
                    </h2>
                    <div className="space-y-4">
                        <div className="rounded-lg bg-zinc-700/50 p-4">
                            <p className="font-bold text-zinc-200">
                                {new Date(event.startTime).toLocaleDateString(
                                    "en-US",
                                    {
                                        weekday: "long",
                                    },
                                )}
                            </p>
                            <p className="text-sm text-zinc-400">
                                {new Date(event.startTime).toLocaleDateString(
                                    "en-US",
                                    {
                                        month: "long",
                                        day: "numeric",
                                        year: "numeric",
                                    },
                                )}
                            </p>
                            <p className="text-brand mt-2 text-lg font-bold">
                                {new Date(event.startTime).toLocaleTimeString(
                                    "en-US",
                                    {
                                        hour: "numeric",
                                        minute: "2-digit",
                                    },
                                )}
                            </p>
                            <p className="text-xs text-zinc-500">Start Time</p>
                        </div>
                        {event.endTime && (
                            <div className="rounded-lg bg-zinc-700/50 p-4">
                                <p className="font-bold text-zinc-200">
                                    {new Date(event.endTime).toLocaleDateString(
                                        "en-US",
                                        {
                                            weekday: "long",
                                        },
                                    )}
                                </p>
                                <p className="text-sm text-zinc-400">
                                    {new Date(event.endTime).toLocaleDateString(
                                        "en-US",
                                        {
                                            month: "long",
                                            day: "numeric",
                                            year: "numeric",
                                        },
                                    )}
                                </p>
                                <p className="text-brand mt-2 text-lg font-bold">
                                    {new Date(event.endTime).toLocaleTimeString(
                                        "en-US",
                                        {
                                            hour: "numeric",
                                            minute: "2-digit",
                                        },
                                    )}
                                </p>
                                <p className="text-xs text-zinc-500">
                                    End Time
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Event Type */}
                <div className="border-brand_stream_team rounded-xl border-2 bg-zinc-900 p-6">
                    <h2 className="text-brand mb-4 flex items-center gap-2 text-lg font-bold">
                        <User className="h-5 w-5" />
                        Event Type
                    </h2>
                    <div className="rounded-lg bg-zinc-700/50 p-4 text-center">
                        <p className="text-lg font-bold text-zinc-200">
                            {EVENT_TYPE_LABELS[
                                (event.eventType || "").toLowerCase()
                            ] || event.eventType}
                        </p>
                        <p className="mt-1 text-sm text-zinc-400">
                            {LOCATION_TYPE_LABELS[
                                (
                                    event.locationType || "online"
                                ).toLowerCase() as keyof typeof LOCATION_TYPE_LABELS
                            ] || "Online"}
                        </p>
                    </div>
                </div>

                {/* Join Event */}
                <div className="border-brand_stream_team rounded-xl border-2 bg-zinc-900 p-6">
                    <h2 className="text-brand mb-4 flex items-center gap-2 text-lg font-bold">
                        <Share2 className="h-5 w-5" />
                        Join Event
                    </h2>
                    {event.joinInstructions && (
                        <div className="mb-4 rounded-lg bg-zinc-700/50 p-4">
                            <p className="mb-1 text-xs font-semibold text-zinc-400">
                                How to Join
                            </p>
                            <p className="text-sm text-zinc-200">
                                {event.joinInstructions}
                            </p>
                        </div>
                    )}
                    {event.joinUrl && (
                        <a
                            href={
                                event.joinUrl.startsWith("http://") ||
                                event.joinUrl.startsWith("https://")
                                    ? event.joinUrl
                                    : `https://${event.joinUrl}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-brand hover:bg-brand/90 flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-medium text-zinc-900 transition-colors"
                        >
                            Join Event <ExternalLink className="h-4 w-4" />
                        </a>
                    )}
                    {!event.joinUrl && !event.joinInstructions && (
                        <p className="text-sm text-zinc-400">
                            Join information will be shared closer to the event.
                        </p>
                    )}
                </div>
            </div>

            {/* Event Information */}
            <div className="border-brand_stream_team mt-6 rounded-xl border-2 bg-zinc-900 p-6">
                <h2 className="text-brand mb-6 text-center text-xl font-bold">
                    Event Information
                </h2>
                <div className="grid gap-6 md:grid-cols-2">
                    {/* Quick Facts */}
                    <div>
                        <h3 className="mb-4 font-bold text-zinc-200">
                            Quick Facts
                        </h3>
                        <dl className="space-y-3">
                            <div className="flex justify-between border-b border-zinc-700 pb-2">
                                <dt className="text-zinc-400">Event Type:</dt>
                                <dd className="text-zinc-200">
                                    {EVENT_TYPE_LABELS[
                                        (event.eventType || "").toLowerCase()
                                    ] || event.eventType}
                                </dd>
                            </div>
                            <div className="flex justify-between border-b border-zinc-700 pb-2">
                                <dt className="text-zinc-400">Status:</dt>
                                <dd>
                                    <span className="rounded-full border border-zinc-600 bg-zinc-700 px-2 py-1 text-xs text-zinc-300">
                                        {STATUS_LABELS[
                                            (event.status || "").toLowerCase()
                                        ] || event.status}
                                    </span>
                                </dd>
                            </div>
                            <div className="flex justify-between border-b border-zinc-700 pb-2">
                                <dt className="text-zinc-400">Location:</dt>
                                <dd className="text-zinc-200">
                                    {LOCATION_TYPE_LABELS[
                                        (
                                            event.locationType || "online"
                                        ).toLowerCase() as keyof typeof LOCATION_TYPE_LABELS
                                    ] || "Online"}
                                </dd>
                            </div>
                            {event.locationDetails && (
                                <div className="flex justify-between border-b border-zinc-700 pb-2">
                                    <dt className="text-zinc-400">
                                        Location Details:
                                    </dt>
                                    <dd className="text-zinc-200">
                                        {event.locationDetails}
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </div>

                    {/* Event Summary */}
                    <div>
                        <h3 className="mb-4 font-bold text-zinc-200">
                            Event Summary
                        </h3>
                        {event.description ? (
                            <p className="whitespace-pre-wrap text-zinc-300">
                                {event.description}
                            </p>
                        ) : event.summary ? (
                            <p className="text-zinc-300">{event.summary}</p>
                        ) : (
                            <p className="text-zinc-400 italic">
                                No description provided.
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Organizer Info */}
            <div className="border-brand_stream_team mt-6 rounded-xl border-2 bg-zinc-900 p-6">
                <h2 className="text-brand mb-4 text-center text-lg font-bold">
                    Event Organizer
                </h2>
                <div className="flex items-center justify-center gap-4">
                    {event.createdBy.player.avatarUrl ? (
                        <img
                            src={event.createdBy.player.avatarUrl}
                            alt={event.createdBy.player.name}
                            className="h-12 w-12 rounded-full border-2 border-yellow-500 object-cover"
                        />
                    ) : (
                        <div className="bg-brand flex h-12 w-12 items-center justify-center rounded-full text-lg font-bold text-zinc-900">
                            {event.createdBy.player.name
                                .charAt(0)
                                .toUpperCase()}
                        </div>
                    )}
                    <div>
                        <Link
                            to={`/team/${event.createdBy.twitchUsername}`}
                            className="font-prviking text-brand hover:text-brand/80 text-lg transition-colors"
                        >
                            {convertLetters(event.createdBy.player.name)}
                        </Link>
                        {event.createdBy.twitchUsername && (
                            <p className="text-sm text-zinc-400">
                                @{event.createdBy.twitchUsername}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
